'use client';

import { type ReactNode, useEffect, useRef } from 'react';

// A little breathing room so anchored section content doesn't sit flush against
// the pinned header's bottom edge.
const SCROLL_PADDING_BREATHING_PX = 8;

interface MarketingHeaderProps {
  children: ReactNode;
}

/**
 * Sticky wrapper that pins the promo banner + nav to the top of the viewport as
 * one unit. The wrapper must stay a plain box (no transform/filter/backdrop) or
 * it would become the containing block for its own sticky positioning and break.
 *
 * It also measures its own rendered height and writes it (plus breathing room)
 * to <html>'s scroll-padding-top, so in-page anchor jumps (#how, ...)
 * land just below the pinned header at every banner state (present, wrapped on
 * mobile, dismissed, or absent). scroll-padding-top must live on the document
 * scroll container (<html>); marketing-home.css can't target <html> (css-scoping
 * guard), so we set it imperatively here and reset it on unmount.
 *
 * The same measurement also exposes the raw header height as --marketing-header-h
 * on <html>, so the full-bleed hero can size itself to the viewport *below* the
 * sticky header (keeping its CTA above the fold) without hardcoding a height that
 * breaks when the promo banner is present, wraps, or is dismissed.
 */
export function MarketingHeader({ children }: MarketingHeaderProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const root = document.documentElement;
    // Guard against a ResizeObserver notification arriving after unmount and
    // re-polluting the shared <html> scroll-padding (it's global, not scoped).
    let active = true;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!active || !entry) return;
      // contentRect.height is the full wrapper height — the wrapper has no
      // border/padding of its own. (Same contentRect read as
      // photo-gallery-view.tsx; note its callback-ref dance is unneeded here —
      // this wrapper mounts unconditionally, so a plain useRef + useEffect
      // can't miss it. Don't "fix" this to match that pattern.)
      const height = entry.contentRect.height;
      root.style.scrollPaddingTop = `${height + SCROLL_PADDING_BREATHING_PX}px`;
      root.style.setProperty('--marketing-header-h', `${height}px`);
    });
    observer.observe(el);

    return () => {
      active = false;
      observer.disconnect();
      root.style.scrollPaddingTop = '';
      root.style.removeProperty('--marketing-header-h');
    };
  }, []);

  return (
    <div ref={ref} className="marketing-header">
      {children}
    </div>
  );
}
